import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ShieldAlert, XCircle } from "lucide-react";
import { toast } from "sonner";
import MarketingNav from "@/components/layout/MarketingNav";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";

const GuardianConsent = () => {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token")?.trim() || "", [params]);
  const [loading, setLoading] = useState<"granted" | "denied" | null>(null);
  const [result, setResult] = useState<"granted" | "denied" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const respond = async (decision: "granted" | "denied") => {
    if (!token) {
      setError("This consent link is missing a token.");
      return;
    }
    setLoading(decision);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc(
        "respond_to_guardian_consent",
        {
          p_token: token,
          p_decision: decision,
        }
      );
      if (rpcError) throw rpcError;
      if (!data || data.ok !== true) {
        throw new Error("Consent could not be recorded.");
      }
      setResult(decision);
      toast.success(
        decision === "granted"
          ? "Consent granted. Thank you."
          : "Consent denied."
      );
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : "Consent link is invalid or expired.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f8faf9] dark:bg-gray-950">
      <MarketingNav />
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 sm:p-8 shadow-sm"
        >
          {!token ? (
            <>
              <ShieldAlert className="w-10 h-10 text-amber-500 mb-4" />
              <h1 className="text-xl font-semibold mb-2">Invalid consent link</h1>
              <p className="text-sm text-gray-500 mb-6">
                Ask your student to open Profile and share a fresh guardian consent link.
              </p>
              <Button asChild variant="outline">
                <Link to="/">Back to Skutopia</Link>
              </Button>
            </>
          ) : result ? (
            <>
              {result === "granted" ? (
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mb-4" />
              ) : (
                <XCircle className="w-10 h-10 text-rose-500 mb-4" />
              )}
              <h1 className="text-xl font-semibold mb-2">
                {result === "granted" ? "Consent granted" : "Consent denied"}
              </h1>
              <p className="text-sm text-gray-500 mb-6">
                {result === "granted"
                  ? "Your student can now use social features on Skutopia Academy."
                  : "Your student will keep learning access, but social features stay limited."}
              </p>
              <Button asChild>
                <Link to="/">Done</Link>
              </Button>
            </>
          ) : (
            <>
              <ShieldAlert className="w-10 h-10 text-skutopia-600 mb-4" />
              <h1 className="text-xl font-semibold mb-2">Guardian consent</h1>
              <p className="text-sm text-gray-500 mb-6">
                A student under 16 asked you to approve social features on Skutopia Academy.
                Learning tools stay available either way.
              </p>
              {error && (
                <p className="text-sm text-rose-600 mb-4">{error}</p>
              )}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  className="flex-1"
                  disabled={loading !== null}
                  onClick={() => respond("granted")}
                >
                  {loading === "granted" ? "Saving..." : "Approve"}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  disabled={loading !== null}
                  onClick={() => respond("denied")}
                >
                  {loading === "denied" ? "Saving..." : "Deny"}
                </Button>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default GuardianConsent;
